import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Settings2,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Beaker,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  useAttributes,
  useCreateAttribute,
  useUpdateAttribute,
  useDeleteAttribute,
  useCreateAttributeOption,
  useUpdateAttributeOption,
  useDeleteAttributeOption,
  useSetOptionIngredients,
  useOptionIngredients,
} from "../api";
import { useIngredients } from "@/features/inventory/api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  EmptyState,
  IngredientManager,
} from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type {
  Attribute,
  AttributeOption,
  CreateAttributeRequest,
  UpdateAttributeRequest,
  CreateAttributeOptionRequest,
  UpdateAttributeOptionRequest,
} from "@/types";

export function AttributesPage() {
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(
    null,
  );
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isOptionDialogOpen, setIsOptionDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<AttributeOption | null>(
    null,
  );
  const [optionAttributeId, setOptionAttributeId] = useState<string | null>(
    null,
  );
  const [expandedAttributes, setExpandedAttributes] = useState<Set<string>>(
    new Set(),
  );
  const [isIngredientsDialogOpen, setIsIngredientsDialogOpen] = useState(false);
  const [ingredientsOptionId, setIngredientsOptionId] = useState<string | null>(
    null,
  );
  const [ingredientsOptionName, setIngredientsOptionName] =
    useState<string>("");

  const { data: attributes, isLoading, error, refetch } = useAttributes();
  const { data: ingredientsData } = useIngredients({ limit: 100 });
  const ingredientsList = ingredientsData?.data ?? [];
  const createAttribute = useCreateAttribute();
  const updateAttribute = useUpdateAttribute();
  const deleteAttribute = useDeleteAttribute();
  const createOption = useCreateAttributeOption();
  const updateOption = useUpdateAttributeOption();
  const deleteOption = useDeleteAttributeOption();
  const setOptionIngredients = useSetOptionIngredients();

  const filteredAttributes = attributes?.filter(
    (attr) => !search || attr.name.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleExpand = (id: string) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const handleCreateAttribute = async (data: CreateAttributeRequest) => {
    try {
      await createAttribute.mutateAsync(data);
      toast.success("Attribute created");
      setIsCreateOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create attribute",
      );
    }
  };

  const handleUpdateAttribute = async (data: UpdateAttributeRequest) => {
    if (!selectedAttribute) return;
    try {
      await updateAttribute.mutateAsync({ id: selectedAttribute.id, data });
      toast.success("Attribute updated");
      setIsEditOpen(false);
      setSelectedAttribute(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update attribute",
      );
    }
  };

  const handleDeleteAttribute = async (id: string) => {
    if (!confirm("Are you sure you want to delete this attribute?")) return;
    try {
      await deleteAttribute.mutateAsync(id);
      toast.success("Attribute deleted");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete attribute",
      );
    }
  };

  const handleCreateOption = async (data: CreateAttributeOptionRequest) => {
    if (!optionAttributeId) return;
    try {
      await createOption.mutateAsync({ attributeId: optionAttributeId, data });
      toast.success("Option added");
      setIsOptionDialogOpen(false);
      setOptionAttributeId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add option");
    }
  };

  const handleUpdateOption = async (data: UpdateAttributeOptionRequest) => {
    if (!selectedOption) return;
    try {
      await updateOption.mutateAsync({ optionId: selectedOption.id, data });
      toast.success("Option updated");
      setIsOptionDialogOpen(false);
      setSelectedOption(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update option",
      );
    }
  };

  const handleDeleteOption = async (optionId: string) => {
    if (!confirm("Are you sure you want to delete this option?")) return;
    try {
      await deleteOption.mutateAsync(optionId);
      toast.success("Option deleted");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete option",
      );
    }
  };

  const handleToggleActive = async (attr: Attribute) => {
    try {
      await updateAttribute.mutateAsync({
        id: attr.id,
        data: { isActive: !attr.isActive },
      });
      toast.success(`Attribute ${attr.isActive ? "deactivated" : "activated"}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update attribute",
      );
    }
  };

  const handleOpenIngredients = (option: AttributeOption) => {
    setIngredientsOptionId(option.id);
    setIngredientsOptionName(option.name);
    setIsIngredientsDialogOpen(true);
  };

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load attributes" onRetry={refetch} />;

  return (
    <div className="container mx-auto p-6">
      <PageHeader
        title="Attributes"
        description="Manage product customization options"
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Attribute
          </Button>
        }
      />
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Attributes ({filteredAttributes?.length || 0})
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search attributes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!filteredAttributes?.length ? (
            <EmptyState
              icon={Settings2}
              title="No attributes found"
              description={
                search
                  ? "Try adjusting your search"
                  : "Get started by adding an attribute"
              }
              action={
                !search && (
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Attribute
                  </Button>
                )
              }
            />
          ) : (
            <div className="space-y-2">
              {filteredAttributes.map((attr) => (
                <div key={attr.id} className="rounded-lg border">
                  <div className="flex items-center justify-between p-4">
                    <button
                      type="button"
                      className="flex items-center gap-3 text-left"
                      onClick={() => toggleExpand(attr.id)}
                    >
                      {expandedAttributes.has(attr.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <div>
                        <p className="font-medium">{attr.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {attr.selectionType === "single"
                            ? "Single selection"
                            : "Multiple selection"}
                          {attr.isRequired && " (Required)"}
                          {" \u2022 "}
                          {attr.options?.length || 0} options
                          {attr.options && attr.options.length > 0 && (
                            <span className="ml-1 text-muted-foreground/70">
                              (
                              {attr.options
                                .slice(0, 3)
                                .map((o) => o.name)
                                .join(", ")}
                              {attr.options.length > 3 && "…"}
                              )
                            </span>
                          )}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={attr.isActive}
                        onCheckedChange={() => handleToggleActive(attr)}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedAttribute(attr);
                              setIsEditOpen(true);
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setOptionAttributeId(attr.id);
                              setSelectedOption(null);
                              setIsOptionDialogOpen(true);
                            }}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Option
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeleteAttribute(attr.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {expandedAttributes.has(attr.id) && (
                    <div className="border-t px-4 pb-4">
                      {attr.options?.length ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Option Name</TableHead>
                              <TableHead>Price Adjustment</TableHead>
                              <TableHead>Cost to Make</TableHead>
                              <TableHead>Default</TableHead>
                              <TableHead>Ingredients</TableHead>
                              <TableHead className="w-[100px]">
                                Actions
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {attr.options.map((option) => (
                              <TableRow key={option.id}>
                                <TableCell>{option.name}</TableCell>
                                <TableCell>
                                  {option.priceAdjustment > 0
                                    ? `+${formatCurrency(option.priceAdjustment, "$")}`
                                    : option.priceAdjustment < 0
                                      ? formatCurrency(
                                          option.priceAdjustment,
                                          "$",
                                        )
                                      : "-"}
                                </TableCell>
                                <TableCell>
                                  {option.costAdjustment > 0
                                    ? formatCurrency(option.costAdjustment, "$")
                                    : "-"}
                                </TableCell>
                                <TableCell>
                                  {option.isDefault && (
                                    <Badge variant="secondary">Default</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleOpenIngredients(option)
                                    }
                                  >
                                    <Beaker className="mr-1 h-3 w-3" />
                                    Manage
                                  </Button>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setSelectedOption(option);
                                        setOptionAttributeId(attr.id);
                                        setIsOptionDialogOpen(true);
                                      }}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        handleDeleteOption(option.id)
                                      }
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="py-4 text-center text-sm text-muted-foreground">
                          No options yet.{" "}
                          <button
                            className="text-primary underline"
                            onClick={() => {
                              setOptionAttributeId(attr.id);
                              setSelectedOption(null);
                              setIsOptionDialogOpen(true);
                            }}
                          >
                            Add one
                          </button>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <AttributeFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={
          handleCreateAttribute as (
            data: CreateAttributeRequest | UpdateAttributeRequest,
          ) => void
        }
        isLoading={createAttribute.isPending}
        title="Create Attribute"
      />
      {selectedAttribute && (
        <AttributeFormDialog
          open={isEditOpen}
          onOpenChange={(open) => {
            setIsEditOpen(open);
            if (!open) setSelectedAttribute(null);
          }}
          attribute={selectedAttribute}
          onSubmit={
            handleUpdateAttribute as (
              data: CreateAttributeRequest | UpdateAttributeRequest,
            ) => void
          }
          isLoading={updateAttribute.isPending}
          title="Edit Attribute"
        />
      )}

      <OptionFormDialog
        open={isOptionDialogOpen}
        onOpenChange={(open) => {
          setIsOptionDialogOpen(open);
          if (!open) {
            setSelectedOption(null);
            setOptionAttributeId(null);
          }
        }}
        option={selectedOption}
        onSubmit={
          (selectedOption ? handleUpdateOption : handleCreateOption) as (
            data: CreateAttributeOptionRequest | UpdateAttributeOptionRequest,
          ) => void
        }
        isLoading={createOption.isPending || updateOption.isPending}
        title={selectedOption ? "Edit Option" : "Add Option"}
      />
      <OptionIngredientsDialog
        open={isIngredientsDialogOpen}
        onOpenChange={(open) => {
          setIsIngredientsDialogOpen(open);
          if (!open) {
            setIngredientsOptionId(null);
            setIngredientsOptionName("");
          }
        }}
        optionId={ingredientsOptionId}
        optionName={ingredientsOptionName}
        ingredientsList={ingredientsList}
        onSave={async (ingredients) => {
          if (!ingredientsOptionId) return;
          try {
            await setOptionIngredients.mutateAsync({
              optionId: ingredientsOptionId,
              ingredients,
            });
            toast.success("Ingredients saved");
            setIsIngredientsDialogOpen(false);
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : "Failed to save ingredients",
            );
          }
        }}
        isLoading={setOptionIngredients.isPending}
      />
    </div>
  );
}

interface AttributeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attribute?: Attribute;
  onSubmit: (data: CreateAttributeRequest | UpdateAttributeRequest) => void;
  isLoading: boolean;
  title: string;
}

function AttributeFormDialog({
  open,
  onOpenChange,
  attribute,
  onSubmit,
  isLoading,
  title,
}: AttributeFormDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    selectionType: "single" as "single" | "multiple",
    isRequired: false,
    minSelections: 0,
    maxSelections: 1,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: attribute?.name || "",
        selectionType: attribute?.selectionType || "single",
        isRequired: attribute?.isRequired || false,
        minSelections: attribute?.minSelections || 0,
        maxSelections: attribute?.maxSelections || 1,
      });
    }
  }, [open, attribute]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData as CreateAttributeRequest);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Size, Milk Type, Extras"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="selectionType">Selection Type</Label>
            <Select
              value={formData.selectionType}
              onValueChange={(value: "single" | "multiple") =>
                setFormData({ ...formData, selectionType: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single Selection</SelectItem>
                <SelectItem value="multiple">Multiple Selection</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border p-4">
            <div>
              <p className="font-medium">Required</p>
              <p className="text-sm text-muted-foreground">
                Customer must select an option
              </p>
            </div>
            <Switch
              checked={formData.isRequired}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isRequired: checked })
              }
            />
          </div>
          {formData.selectionType === "multiple" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="minSelections">Min Selections</Label>
                <Input
                  id="minSelections"
                  type="number"
                  min={0}
                  value={formData.minSelections || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minSelections: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="e.g., 0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxSelections">Max Selections</Label>
                <Input
                  id="maxSelections"
                  type="number"
                  min={1}
                  value={formData.maxSelections || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxSelections: parseInt(e.target.value) || 1,
                    })
                  }
                  placeholder="e.g., 3"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface OptionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  option?: AttributeOption | null;
  onSubmit: (
    data: CreateAttributeOptionRequest | UpdateAttributeOptionRequest,
  ) => void;
  isLoading: boolean;
  title: string;
}

function OptionFormDialog({
  open,
  onOpenChange,
  option,
  onSubmit,
  isLoading,
  title,
}: OptionFormDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    priceAdjustment: 0,
    costAdjustment: 0,
    isDefault: false,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: option?.name || "",
        priceAdjustment: (option?.priceAdjustment || 0) / 100,
        costAdjustment: (option?.costAdjustment || 0) / 100,
        isDefault: option?.isDefault || false,
      });
    }
  }, [open, option]);

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();

    onSubmit({
      name: formData.name,
      priceAdjustment: Math.round(formData.priceAdjustment * 100), // Convert to cents
      costAdjustment: Math.round(formData.costAdjustment * 100), // Convert to cents
      isDefault: formData.isDefault,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="optionName">Name</Label>
            <Input
              id="optionName"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Small, Oat Milk, Extra Shot"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="priceAdjustment">Price Adjustment ($)</Label>
            <Input
              id="priceAdjustment"
              type="number"
              step="0.01"
              value={formData.priceAdjustment || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priceAdjustment: parseFloat(e.target.value) || 0,
                })
              }
              placeholder="e.g., 0.50"
            />
            <p className="text-xs text-muted-foreground">
              Use positive for extra charge, negative for discount
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="costAdjustment">Cost to Make ($)</Label>
            <Input
              id="costAdjustment"
              type="number"
              step="0.01"
              min="0"
              value={formData.costAdjustment || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  costAdjustment: parseFloat(e.target.value) || 0,
                })
              }
              placeholder="e.g., 0.30"
            />
            <p className="text-xs text-muted-foreground">
              What this option costs the business per unit, used for profit
              reporting
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border p-4">
            <div>
              <p className="font-medium">Default Option</p>
              <p className="text-sm text-muted-foreground">
                Pre-selected when adding to cart
              </p>
            </div>
            <Switch
              checked={formData.isDefault}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isDefault: checked })
              }
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface OptionIngredientsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  optionId: string | null;
  optionName: string;
  ingredientsList: { id: string; name: string; unit: string }[];
  onSave: (
    ingredients: { ingredientId: string; quantityUsed: number }[],
  ) => void;
  isLoading: boolean;
}

function OptionIngredientsDialog({
  open,
  onOpenChange,
  optionId,
  optionName,
  ingredientsList,
  onSave,
  isLoading,
}: OptionIngredientsDialogProps) {
  const [ingredients, setIngredients] = useState<
    { ingredientId: string; quantityUsed: number }[]
  >([]);
  const { data: existing, isFetching } = useOptionIngredients(
    open ? optionId : null,
  );

  useEffect(() => {
    if (!open) return;
    setIngredients(
      (existing ?? []).map((row) => ({
        ingredientId: row.ingredientId,
        quantityUsed: row.quantityUsed,
      })),
    );
  }, [open, optionId, existing]);

  const handleSave = () => {
    const invalid = ingredients.find(
      (i) => !i.quantityUsed || i.quantityUsed < 1,
    );
    if (invalid) {
      toast.error("Each ingredient quantity must be at least 1");

      return;
    }
    onSave(ingredients);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Beaker className="h-5 w-5 text-primary" />
            Ingredients for "{optionName}"
          </DialogTitle>
          <DialogDescription>
            These ingredients are deducted from inventory whenever a product
            using this option is sold.
          </DialogDescription>
        </DialogHeader>
        <div className="-mx-2 max-h-[60vh] overflow-y-auto px-2 py-2">
          {isFetching ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading ingredients…
            </div>
          ) : (
            <IngredientManager
              ingredients={ingredients}
              availableIngredients={ingredientsList}
              onChange={setIngredients}
              title=""
              description=""
              compact
            />
          )}
        </div>
        <DialogFooter className="border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isFetching}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save Ingredients"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
